import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, AlertTriangle, Lock } from "lucide-react";
import { cn } from "../../../utils/tailwindUtils";
import { STAGE_BADGE } from "../../../utils/leadStageBadge";
import { fmtCell, fmtMetric, fmtNumber, isMoneyColumn } from "./formatters";

/** Columns whose values are lead stages get the usual stage badge treatment. */
const STAGE_COLUMNS = ["lead_stage", "to_status", "from_status", "current_stage"];

const SummaryChips = ({ metrics }) => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {metrics.map((m) => (
            <div
                key={m.label}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/50 px-3 py-2"
            >
                <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                    {m.label}
                </p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {fmtMetric(m.value, m.format, { compact: true })}
                </p>
                {m.hint && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{m.hint}</p>
                )}
            </div>
        ))}
    </div>
);

/** `{label: number}` breakdown → labelled bars. */
const BreakdownBars = ({ data }) => {
    const entries = Object.entries(data);
    const max = Math.max(...entries.map(([, v]) => Number(v) || 0), 1);
    return (
        <div className="space-y-1.5">
            {entries.map(([label, value]) => (
                <div key={label} className="flex items-center gap-2">
                    <span className="w-32 shrink-0 text-[11px] text-gray-600 dark:text-gray-300 truncate" title={label}>
                        {label || "Not Set"}
                    </span>
                    <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-indigo-500/80 dark:bg-indigo-400/80"
                            style={{ width: `${((Number(value) || 0) / max) * 100}%` }}
                        />
                    </div>
                    <span className="w-14 shrink-0 text-right text-[11px] font-medium text-gray-700 dark:text-gray-200">
                        {fmtNumber(value)}
                    </span>
                </div>
            ))}
        </div>
    );
};

/** `[{...}, {...}]` breakdown → compact table using the row keys as headers. */
const BreakdownTable = ({ rows }) => {
    const keys = Object.keys(rows[0]).filter(k => !k.endsWith("_id"));
    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-[11px]">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr className="text-left">
                        {keys.map(k => (
                            <th key={k} className="px-2.5 py-1.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                {k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rows.slice(0, 15).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                            {keys.map(k => (
                                <td key={k} className="px-2.5 py-1.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                    {fmtCell(row[k], k)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Breakdown = ({ label, payload }) => {
    const isDict = payload && !Array.isArray(payload) && typeof payload === "object";
    const isRows = Array.isArray(payload) && payload.length > 0 && typeof payload[0] === "object";
    if (!isDict && !isRows) return null;
    return (
        <div className="space-y-2">
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {label}
            </p>
            {isDict ? <BreakdownBars data={payload} /> : <BreakdownTable rows={payload} />}
        </div>
    );
};

const DetailTable = ({ columns, rows }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="max-h-[420px] overflow-auto print:max-h-none print:overflow-visible">
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 dark:bg-gray-700/60 text-left">
                        <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                className={cn(
                                    "px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap",
                                    isMoneyColumn(col.key) && "text-right"
                                )}
                            >
                                {col.title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                            <td className="px-3 py-2 text-gray-400 dark:text-gray-500">{idx + 1}</td>
                            {columns.map(col => {
                                const value = row[col.key];
                                const isStage = STAGE_COLUMNS.includes(col.key) && value;
                                return (
                                    <td
                                        key={col.key}
                                        className={cn(
                                            "px-3 py-2 text-gray-600 dark:text-gray-300 max-w-[220px] truncate",
                                            isMoneyColumn(col.key) && "text-right font-medium text-gray-800 dark:text-gray-100"
                                        )}
                                        title={value === null || value === undefined ? "" : String(value)}
                                    >
                                        {isStage ? (
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                STAGE_BADGE[value] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                                            )}>
                                                {value}
                                            </span>
                                        ) : fmtCell(value, col.key)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const Skeleton = () => (
    <div className="space-y-2 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[0, 1, 2, 3].map(i => (
                <div key={i} className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800" />
            ))}
        </div>
        <div className="h-28 rounded-lg bg-gray-100 dark:bg-gray-800" />
    </div>
);

/**
 * One report inside the All-in-One page. Every section is rendered from the
 * same normalised payload, so a new backend section needs no UI work here.
 */
const ReportSectionCard = ({ index, section, loading, defaultOpen = true, onToggle }) => {
    const [open, setOpen] = useState(defaultOpen);
    const rows = section?.rows || [];
    const columns = section?.columns || [];

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (onToggle) onToggle(next);
    };

    return (
        <section
            id={`section-${section.key}`}
            className="scroll-mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 print:break-inside-avoid"
        >
            {/* header */}
            <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <button
                    onClick={toggle}
                    className="mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 print:hidden"
                    aria-label={open ? "Collapse" : "Expand"}
                >
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">
                            {String(index).padStart(2, "0")}
                        </span>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {section.label}
                        </h2>
                        {section.status === "success" && section.total_rows > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {fmtNumber(section.total_rows)} rows
                            </span>
                        )}
                        {section.status === "error" && (
                            <span className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                                <AlertTriangle className="h-3 w-3" /> unavailable
                            </span>
                        )}
                        {section.status === "denied" && (
                            <span className="flex items-center gap-1 text-[10px] text-gray-400">
                                <Lock className="h-3 w-3" /> no access
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {section.description}
                    </p>
                </div>
                {section.route && (
                    <Link
                        to={section.route}
                        className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap print:hidden"
                    >
                        Full report <ExternalLink className="h-3 w-3" />
                    </Link>
                )}
            </div>

            {/* body */}
            {open && (
                <div className="p-4 space-y-4">
                    {loading && <Skeleton />}

                    {!loading && section.status !== "success" && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {section.message || "This report could not be loaded."}
                        </p>
                    )}

                    {!loading && section.status === "success" && (
                        <>
                            {section.summary?.length > 0 && <SummaryChips metrics={section.summary} />}

                            {Object.keys(section.extra || {}).length > 0 && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {Object.entries(section.extra).map(([label, payload]) => (
                                        <Breakdown key={label} label={label} payload={payload} />
                                    ))}
                                </div>
                            )}

                            {columns.length > 0 && rows.length > 0 ? (
                                <>
                                    <DetailTable columns={columns} rows={rows} />
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                        Showing {fmtNumber(rows.length)} of {fmtNumber(section.total_rows)} rows
                                        {section.truncated && " — download the Excel export for the complete list"}
                                    </p>
                                </>
                            ) : (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                    No records for the selected filters.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

export default ReportSectionCard;
